import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import store from '../store';
import { addInitUnAnsweredConversation } from '../store/action/unanswered-convertations-action';
import { addInitAnsweredConversation } from '../store/action/answered-conversations-action';
import { getDailyClosedChat } from '../store/action/update-chat-metrics-action';
import { useSocketEvents } from './socket-utils';

const SYNC_INTERVAL_MS = 60000; // 60 segundos (De quanto em quanto tempo vai ficar verificando se tem algo novo)
const SYNC_LOG_PREFIX = '[QueueSync]';

/**
 * Extrai os IDs dos chats de um array de conversas.
 * @param {Array<*>} conversations
 * @returns {Set<string>}
 */
function extractChatIds(conversations) {
  if (!conversations) return new Set();
  return new Set(
    conversations
      .map(function (c) {
        return c._id || c.id;
      })
      .filter(Boolean)
  );
}

/**
 * Compara os dados do servidor com o estado local do Redux e registra qualquer dessincronização.
 * Retorna true se a dessincronização foi detectada.
 * @param {object} serverData
 * @param {object} localAnswered
 * @param {object} localUnanswered
 * @returns {boolean}
 */
function detectAndLogDesync(serverData, localAnswered, localUnanswered) {
  const serverAnsweredIds = extractChatIds(serverData.minhasConversas);
  const serverUnansweredIds = extractChatIds(
    serverData.conversasDasMinhasFilas
  );
  const localAnsweredIds = extractChatIds(localAnswered.conversations);
  const localUnansweredIds = extractChatIds(localUnanswered.conversations);

  // Chats no servidor mas ausentes localmente (eventos de socket perdidos)
  var missingAnswered = Array.from(serverAnsweredIds).filter(function (id) {
    return !localAnsweredIds.has(id);
  });
  var missingUnanswered = Array.from(serverUnansweredIds).filter(function (id) {
    return !localUnansweredIds.has(id);
  });

  // Chats locais que não existem mais no servidor (chats fantasmas/obsoletos)
  var staleAnswered = Array.from(localAnsweredIds).filter(function (id) {
    return !serverAnsweredIds.has(id);
  });
  var staleUnanswered = Array.from(localUnansweredIds).filter(function (id) {
    return !serverUnansweredIds.has(id);
  });

  const hasMissing = missingAnswered.length > 0 || missingUnanswered.length > 0;
  const hasStale = staleAnswered.length > 0 || staleUnanswered.length > 0;

  if (hasMissing || hasStale) {
    const agentLogin = localStorage.getItem('login') || 'unknown';
    const timestamp = new Date().toISOString();

    console.warn(
      `${SYNC_LOG_PREFIX} Dessincronização detectada em ${timestamp}:\n` +
        `  Agente: ${agentLogin}\n` +
        (missingAnswered.length
          ? `  Conversas respondidas ausentes (eventos perdidos): ${missingAnswered.join(
              ', '
            )}\n`
          : '') +
        (missingUnanswered.length
          ? `  Conversas não respondidas ausentes (eventos perdidos): ${missingUnanswered.join(
              ', '
            )}\n`
          : '') +
        (staleAnswered.length
          ? `  Conversas respondidas obsoletas (fantasmas): ${staleAnswered.join(
              ', '
            )}\n`
          : '') +
        (staleUnanswered.length
          ? `  Conversas não respondidas obsoletas (fantasmas): ${staleUnanswered.join(
              ', '
            )}\n`
          : '')
    );

    return true;
  }

  console.log(
    `${SYNC_LOG_PREFIX} Sincronização concluída — nenhuma dessincronização detectada (${new Date().toISOString()})`
  );
  return false;
}

/**
 * Hook que sincroniza periodicamente o estado da fila de chat com o backend.
 *
 * A cada 60 segundos (e imediatamente quando a aba recupera a visibilidade), ele emite
 * `conversas_do_atendente` via `socketConversation`, compara a resposta
 * com o estado atual do Redux, registra qualquer dessincronização, reconcilia o estado e
 * força um re-join do Socket.io se inconsistências forem encontradas.
 *
 * Tarefa de referência: https://app.clickup.com/t/86afr6yfp
 *
 * @param {object} socketConversation - The legacy socket connection
 * @param {object} socketChatTS - The new chat TS socket connection
 */
export function useQueueSyncHeartbeat(socketConversation, socketChatTS) {
  const answeredConversations = useSelector(
    (state) => state.answeredConversation
  );
  const unAnsweredConversations = useSelector(
    (state) => state.unAnsweredConversation
  );

  // Mantém refs para que o callback sempre veja o estado atual sem recriar
  const answeredRef = useRef(answeredConversations);
  const unansweredRef = useRef(unAnsweredConversations);

  useEffect(() => {
    answeredRef.current = answeredConversations;
  }, [answeredConversations]);

  useEffect(() => {
    unansweredRef.current = unAnsweredConversations;
  }, [unAnsweredConversations]);

  /**
   * Força o socketChatTS a desconectar e reconectar, o que aciona o
   * handler `connect` no socketConversation que re-emite `atendente_login`
   * e reconecta a todas as salas.
   */
  const forceReJoin = useCallback(() => {
    console.warn(
      `${SYNC_LOG_PREFIX} Forçando reconexão do Socket.io devido à dessincronização detectada`
    );

    if (socketChatTS && socketChatTS.connected) {
      socketChatTS.disconnect();
      socketChatTS.connect();
    }

    if (socketConversation && socketConversation.connected) {
      socketConversation.disconnect();
      socketConversation.connect();
    }
  }, [socketChatTS, socketConversation]);

  /**
   * Realiza um ciclo de sincronização único: emite a solicitação de pull e lida com a resposta.
   */
  const performSync = useCallback(() => {
    if (!socketConversation || !socketConversation.connected) {
      console.warn(
        `${SYNC_LOG_PREFIX} Pulando sincronização — socketConversation não conectado`
      );
      return;
    }

    const agentLogin = localStorage.getItem('login');
    if (!agentLogin) {
      console.warn(
        `${SYNC_LOG_PREFIX} Pulando sincronização — nenhum login de atendente encontrado`
      );
      return;
    }

    socketConversation.emit('conversas_do_atendente', {
      atendente_login: agentLogin
    });
  }, [socketConversation]);

  useSocketEvents(socketConversation, {
    conversas_do_atendente: async (data) => {
      try {
        const desyncDetected = detectAndLogDesync(
          data,
          answeredRef.current,
          unansweredRef.current
        );

        // Reconciliação: sobrescreve o estado do Redux com dados frescos do servidor
        await store.dispatch(
          addInitUnAnsweredConversation(data.conversasDasMinhasFilas)
        );
        await store.dispatch(addInitAnsweredConversation(data.minhasConversas));
        await store.dispatch(getDailyClosedChat(data.conversasEncerradas));

        // Força o re-join se o desync foi detectado
        if (desyncDetected) {
          forceReJoin();
        }
      } catch (error) {
        console.error(
          `${SYNC_LOG_PREFIX} Erro durante a reconciliação da sincronização:`,
          error
        );
      }
    }
  });

  // Set interval principal
  useEffect(() => {
    if (!socketConversation || !socketChatTS) return;

    const intervalId = setInterval(performSync, SYNC_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [socketConversation, socketChatTS, performSync]);

  // Detecta mudança de visibilidade: sincronização imediata quando a aba volta do background
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log(
          `${SYNC_LOG_PREFIX} A aba ficou visível — acionando sincronização imediata`
        );
        performSync();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [performSync]);
}
